// src/api/liveSessionsApi.js
//
// Live Sessions API calls. Faculty endpoints use the platform's regular
// `api` instance (Bearer access_token attached automatically by the
// existing interceptor). Student endpoints reuse the AskOS student token
// convention (askos_token in localStorage, passed explicitly per call),
// matching frontend/src/askos/askosApi.js.
import api from './api';
import { askosAuthHeader } from '../askos/askosApi';

// ── Faculty ──────────────────────────────────────────────────────────────

// Lighter-weight than api.js's getMyFacultyProjects (Teaching Copilot) —
// only requires the faculty assignment + completed CKB, not the separate
// content-review 'approved' status, since scheduling a class isn't part
// of that workflow.
export const listSchedulableProjects = (slug) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/schedulable-projects`).then(r => r.data);

export const scheduleLiveSession = (slug, payload) =>
  api.post(`/tenant/${slug}/faculty/live-sessions`, payload).then(r => r.data);

export const listMyLiveSessions = (slug, status = null) =>
  api.get(`/tenant/${slug}/faculty/live-sessions`, { params: status ? { status } : {} }).then(r => r.data);

export const getLiveSessionFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}`).then(r => r.data);

export const updateLiveSession = (slug, sessionId, payload) =>
  api.put(`/tenant/${slug}/faculty/live-sessions/${sessionId}`, payload).then(r => r.data);

export const cancelLiveSession = (slug, sessionId, reason = '') =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/cancel`, { reason }).then(r => r.data);

export const goLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/go-live`).then(r => r.data);

export const getHostToken = (slug, sessionId) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/host-token`).then(r => r.data);

export const endLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/end`).then(r => r.data);

export const getRelayStatus = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/relay-status`).then(r => r.data);

export const pauseLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/pause`).then(r => r.data);

export const resumeLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/resume`).then(r => r.data);

export const getRecordingUrlFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/recording-url`).then(r => r.data);

export const listQuestionsFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/questions`).then(r => r.data);

export const answerQuestion = (slug, sessionId, questionId, payload) =>
  api.post(`/tenant/${slug}/faculty/live-sessions/${sessionId}/questions/${questionId}/answer`, payload).then(r => r.data);

export const getFeedbackSummaryFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/feedback-summary`).then(r => r.data);

export const getAttendanceSummaryFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/attendance-summary`).then(r => r.data);

export const getTranslatedRecordingsFaculty = (slug, sessionId) =>
  api.get(`/tenant/${slug}/faculty/live-sessions/${sessionId}/translated-recordings`).then(r => r.data);

// ── Student (AskOS) ──────────────────────────────────────────────────────

export const listLiveSessionsStudent = (slug, status = null) =>
  api.get(`/tenant/${slug}/askos/live-sessions`, {
    headers: askosAuthHeader(),
    params: status ? { status } : {},
  }).then(r => r.data);

export const getLiveSessionStudent = (slug, sessionId) =>
  api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}`, { headers: askosAuthHeader() }).then(r => r.data);

export const joinLiveSession = (slug, sessionId, language = null) =>
  api.post(
    `/tenant/${slug}/askos/live-sessions/${sessionId}/join`,
    language ? { language } : {},
    { headers: askosAuthHeader() }
  ).then(r => r.data);

export const getRecordingUrlStudent = (slug, sessionId) =>
  api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}/recording-url`, { headers: askosAuthHeader() }).then(r => r.data);

export const heartbeatLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/askos/live-sessions/${sessionId}/heartbeat`, {}, { headers: askosAuthHeader() }).then(r => r.data);

export const leaveLiveSession = (slug, sessionId) =>
  api.post(`/tenant/${slug}/askos/live-sessions/${sessionId}/leave`, {}, { headers: askosAuthHeader() }).then(r => r.data);

export const askLiveSessionQuestion = (slug, sessionId, questionText) =>
  api.post(`/tenant/${slug}/askos/live-sessions/${sessionId}/questions`, { question_text: questionText }, { headers: askosAuthHeader() }).then(r => r.data);

export const listMyQuestions = (slug, sessionId) =>
  api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}/questions`, { headers: askosAuthHeader() }).then(r => r.data);

export const submitLiveSessionFeedback = (slug, sessionId, rating, comments = '') =>
  api.post(`/tenant/${slug}/askos/live-sessions/${sessionId}/feedback`, { rating, comments }, { headers: askosAuthHeader() }).then(r => r.data);

export const getMyAttendance = (slug) =>
  api.get(`/tenant/${slug}/askos/live-sessions/my-attendance`, { headers: askosAuthHeader() }).then(r => r.data);

// ── Lecture Notes Search (standalone — see plan doc Part 5E; local ─────
// lexical/keyword matching, not wired into AskOS Chat) ───────────────────

export const searchLectureNotes = (slug, q, projectId = null) =>
  api.get(`/tenant/${slug}/askos/lecture-notes/search`, {
    headers: askosAuthHeader(),
    params: { q, ...(projectId ? { project_id: projectId } : {}) },
  }).then(r => r.data);

export const getTranslatedRecordingsStudent = (slug, sessionId) =>
  api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}/translated-recordings`, { headers: askosAuthHeader() }).then(r => r.data);

// ── Live transcript (student panel) ─────────────────────────────────────

export const getLiveSessionTranscript = (slug, sessionId, language, sinceId = 0) =>
  api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}/transcript`, {
    headers: askosAuthHeader(),
    params: { language, since_id: sinceId },
  }).then(r => r.data);

// Auth here is header-based (askos_token), so a plain <a href> download
// won't carry it — fetch as a blob and trigger the save client-side.
export const downloadLiveSessionTranscript = async (slug, sessionId, language) => {
  const response = await api.get(`/tenant/${slug}/askos/live-sessions/${sessionId}/transcript/download`, {
    headers: askosAuthHeader(),
    params: { language },
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `transcript-${sessionId}-${language}.txt`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
